// Geo-location utilities for detecting user's country and preferred language

// Mapping of countries to their default language
const countryToLanguage = {
  // French-speaking countries
  FR: 'fr', // France
  BE: 'fr', // Belgium (default to French)
  CH: 'fr', // Switzerland (default to French)
  LU: 'fr', // Luxembourg
  MC: 'fr', // Monaco
  // French-speaking African countries
  TN: 'fr', // Tunisia
  MA: 'fr', // Morocco  
  DZ: 'fr', // Algeria
  SN: 'fr', // Senegal
  CI: 'fr', // Côte d'Ivoire
  CM: 'fr', // Cameroon
  BF: 'fr', // Burkina Faso
  NE: 'fr', // Niger
  ML: 'fr', // Mali
  BJ: 'fr', // Benin
  TG: 'fr', // Togo
  GA: 'fr', // Gabon
  CG: 'fr', // Congo
  CD: 'fr', // Democratic Republic of Congo
  RW: 'fr', // Rwanda
  BI: 'fr', // Burundi
  DJ: 'fr', // Djibouti
  KM: 'fr', // Comoros
  MG: 'fr', // Madagascar
  MU: 'fr', // Mauritius
  SC: 'fr', // Seychelles
  
  // Spanish-speaking countries
  ES: 'es', // Spain
  MX: 'es', // Mexico
  AR: 'es', // Argentina
  CO: 'es', // Colombia
  PE: 'es', // Peru
  VE: 'es', // Venezuela
  CL: 'es', // Chile
  EC: 'es', // Ecuador
  GT: 'es', // Guatemala
  CU: 'es', // Cuba
  BO: 'es', // Bolivia
  DO: 'es', // Dominican Republic
  HN: 'es', // Honduras
  PY: 'es', // Paraguay
  SV: 'es', // El Salvador
  NI: 'es', // Nicaragua
  CR: 'es', // Costa Rica
  PA: 'es', // Panama
  UY: 'es', // Uruguay
  GQ: 'es', // Equatorial Guinea
  
  // English as default for these countries
  US: 'en', // United States
  GB: 'en', // United Kingdom
  CA: 'en', // Canada (default to English)
  AU: 'en', // Australia
  NZ: 'en', // New Zealand
  IE: 'en', // Ireland
  ZA: 'en', // South Africa
  IN: 'en', // India
  PK: 'en', // Pakistan
  NG: 'en', // Nigeria
  KE: 'en', // Kenya
  GH: 'en', // Ghana
  UG: 'en', // Uganda
  ZW: 'en', // Zimbabwe
  ZM: 'en', // Zambia
  BW: 'en', // Botswana
  NA: 'en', // Namibia
  LS: 'en', // Lesotho
  SZ: 'en', // Eswatini
  MW: 'en', // Malawi
  TZ: 'en', // Tanzania
  ET: 'en', // Ethiopia
  SD: 'en', // Sudan
  SS: 'en', // South Sudan
  LR: 'en', // Liberia
  SL: 'en', // Sierra Leone
  GM: 'en', // Gambia
  
  // Middle East - default to English
  AE: 'en', // United Arab Emirates
  SA: 'en', // Saudi Arabia
  QA: 'en', // Qatar
  KW: 'en', // Kuwait
  BH: 'en', // Bahrain
  OM: 'en', // Oman
  JO: 'en', // Jordan
  LB: 'en', // Lebanon
  IQ: 'en', // Iraq
  YE: 'en', // Yemen
  SY: 'en', // Syria
  IL: 'en', // Israel
  TR: 'en', // Turkey
  
  // Asia - default to English
  SG: 'en', // Singapore
  MY: 'en', // Malaysia
  PH: 'en', // Philippines
  ID: 'en', // Indonesia
  TH: 'en', // Thailand
  VN: 'en', // Vietnam
  JP: 'en', // Japan
  KR: 'en', // South Korea
  CN: 'en', // China
  HK: 'en', // Hong Kong
  TW: 'en', // Taiwan
  
  // Europe - other countries default to English
  DE: 'en', // Germany
  IT: 'en', // Italy
  NL: 'en', // Netherlands
  SE: 'en', // Sweden
  NO: 'en', // Norway
  DK: 'en', // Denmark
  FI: 'en', // Finland
  PL: 'en', // Poland
  PT: 'en', // Portugal
  GR: 'en', // Greece
  CZ: 'en', // Czech Republic
  HU: 'en', // Hungary
  RO: 'en', // Romania
  BG: 'en', // Bulgaria
  HR: 'en', // Croatia
  RS: 'en', // Serbia
  SK: 'en', // Slovakia
  SI: 'en', // Slovenia
  LT: 'en', // Lithuania
  LV: 'en', // Latvia
  EE: 'en', // Estonia
  
  // Default
  DEFAULT: 'en'
};

// Cache for storing detected country
let detectedCountryCache = null;

/**
 * Detect user's country using IP geolocation
 * @returns {Promise<string>} Country code (e.g., 'FR', 'US')
 */
export const detectUserCountry = async () => {
  // Return cached value if available
  if (detectedCountryCache) {
    return detectedCountryCache;
  }

  try {
    // Try multiple geo-location services for reliability
    const geoServices = [
      // Primary service - ipapi.co (no API key required, 1000 requests/day)
      async () => {
        const controller = new AbortController();
        const timeoutId = setTimeout(() => controller.abort(), 3000);
        
        try {
          const response = await fetch('https://ipapi.co/json/', {
            signal: controller.signal
          });
          clearTimeout(timeoutId);
          const data = await response.json();
          return data.country_code;
        } catch (error) {
          clearTimeout(timeoutId);
          throw error;
        }
      },
      
      // Fallback service - ipinfo.io (no API key required for basic usage)
      async () => {
        const controller = new AbortController();
        const timeoutId = setTimeout(() => controller.abort(), 3000);
        
        try {
          const response = await fetch('https://ipinfo.io/json', {
            signal: controller.signal
          });
          clearTimeout(timeoutId);
          const data = await response.json();
          // ipinfo returns country as 2-letter code
          return data.country;
        } catch (error) {
          clearTimeout(timeoutId);
          throw error;
        }
      }
    ];

    // Try each service until one succeeds
    for (const service of geoServices) {
      try {
        const countryCode = await service();
        if (countryCode) {
          detectedCountryCache = countryCode;
          return countryCode;
        }
      } catch (serviceError) {
        console.warn('Geo-location service failed:', serviceError);
        continue;
      }
    }
  } catch (error) {
    console.error('Failed to detect user country:', error);
  }

  // Fallback to browser language if geo-location fails
  const browserLang = navigator.language || navigator.userLanguage;
  if (browserLang) {
    // Extract country from browser language (e.g., 'fr-FR' -> 'FR')
    const parts = browserLang.split('-');
    if (parts.length > 1) {
      const countryCode = parts[1].toUpperCase();
      detectedCountryCache = countryCode;
      return countryCode;
    }
  }

  // Default to 'US' if all else fails
  detectedCountryCache = 'US';
  return 'US';
};

/**
 * Get default language for a country
 * @param {string} countryCode - Country code (e.g., 'FR', 'US')
 * @returns {string} Language code ('fr', 'en', or 'es')
 */
export const getDefaultLanguageForCountry = (countryCode) => {
  const upperCountryCode = countryCode?.toUpperCase();
  return countryToLanguage[upperCountryCode] || countryToLanguage.DEFAULT;
};

/**
 * Detect user's preferred language based on location
 * @returns {Promise<string>} Language code ('fr', 'en', or 'es')
 */
export const detectUserLanguage = async () => {
  try {
    const country = await detectUserCountry();
    return getDefaultLanguageForCountry(country);
  } catch (error) {
    console.error('Failed to detect user language:', error);
    return 'en'; // Default to English
  }
};

/**
 * Clear the country cache (useful for testing)
 */
export const clearGeoCache = () => {
  detectedCountryCache = null;
};